import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mountain, ArrowRight, Check, MapPin, Home, Shield, Star, BarChart3, Users, Globe } from 'lucide-react';
import StateSelector from '../components/StateSelector';
import HimalayanPeaksFooter from '../components/HimalayanPeaksFooter';

const DISTRICTS = [
  { id: 'jorhat', name: 'Jorhat & Majuli', homestays: 320, valleys: ['Majuli Island', 'Satras', 'Tea Estates', 'Gibbon WLS'], altitude: '80–200m', highlight: true, division: 'Upper Assam' },
  { id: 'kaziranga', name: 'Kaziranga', homestays: 280, valleys: ['Kaziranga NP', 'Kohora', 'Bagori', 'Agoratoli'], altitude: '40–120m', highlight: true, division: 'Central Assam' },
  { id: 'kamrup', name: 'Kamrup & Guwahati', homestays: 250, valleys: ['Kamakhya Temple', 'Umananda', 'Pobitora WLS', 'Deepor Beel'], altitude: '50–300m', highlight: true, division: 'Lower Assam' },
  { id: 'dibrugarh', name: 'Dibrugarh', homestays: 200, valleys: ['Tea Bungalows', 'Joypur Rainforest', 'Chabua', 'Naharkatia'], altitude: '100–200m', highlight: true, division: 'Upper Assam' },
  { id: 'sonitpur', name: 'Sonitpur & Tezpur', homestays: 180, valleys: ['Nameri NP', 'Agnigarh Hill', 'Bhalukpong', 'Chariduar'], altitude: '50–500m', highlight: true, division: 'Central Assam' },
  { id: 'sivasagar', name: 'Sivasagar', homestays: 150, valleys: ['Rang Ghar', 'Talatal Ghar', 'Sivadol', 'Ahom Ruins'], altitude: '80–150m', highlight: false, division: 'Upper Assam' },
  { id: 'nagaon', name: 'Nagaon', homestays: 120, valleys: ['Laokhowa WLS', 'Chapanala', 'Raha', 'Wetlands'], altitude: '50–100m', highlight: false, division: 'Central Assam' },
  { id: 'bongaigaon', name: 'Bongaigaon & Chirang', homestays: 80, valleys: ['Manas NP', 'Bhutan Foothills', 'Bijni', 'Sidli'], altitude: '40–300m', highlight: false, division: 'Lower Assam' },
];

const TOTAL_HOMESTAYS = DISTRICTS.reduce((sum, d) => sum + d.homestays, 0);

const TIERS = [
  {
    name: 'Registered',
    tagline: 'State-Licensed Homestays',
    desc: 'Every homestay registered with the Assam Tourism Development Corporation, from Brahmaputra river islands to highland tea estates, digitized on one platform.',
    icon: Home,
    color: '#8B9DAF',
    features: ['License verification badge', 'Location on region map', 'Basic contact listing', 'Searchable by district & river zone'],
  },
  {
    name: 'Silver',
    tagline: 'GoHimalayan Quality Assured',
    desc: 'Registered homestays editorially visited and quality-verified. Verified tea-bungalow stays, river-island retreats, and traveller-ready listings.',
    icon: Shield,
    color: '#A8B8C8',
    features: ['On-ground quality verification', 'Professional photography', 'AI-curated descriptions', 'Review management', 'Priority in search results'],
  },
  {
    name: 'Gold',
    tagline: 'The GoHimalayan Collective',
    desc: 'Premium partners with full storytelling pages, active marketing, direct booking, and dedicated account management. The hidden gems of the Brahmaputra valley.',
    icon: Star,
    color: '#D4A853',
    features: ['Luxury storytelling page', 'Direct booking integration', 'Social media marketing', 'Revenue analytics dashboard', 'Dedicated account manager', 'Featured in campaigns'],
  },
];

const VALUE_PROPS = [
  { icon: Globe, title: 'Digital Registry', desc: 'Every licensed homestay from Majuli\'s river islands to Kaziranga\'s grasslands — searchable, verified, bookable on a single platform.' },
  { icon: BarChart3, title: 'Demand Intelligence', desc: 'Real-time traveller search data, rhino-safari season trends, and Bihu festival occupancy insights for Assam tourism planning.' },
  { icon: Users, title: 'Host Enablement', desc: 'Training, photography, and digital literacy — empowering tea-garden communities, Mishing river-island families, and Ahom heritage hosts.' },
  { icon: Shield, title: 'Quality Assurance', desc: 'On-ground verification ensuring each homestay honours the colonial tea-bungalow heritage, river traditions, and Assamese warmth.' },
];

function AssamMap({ activeDistrict, setActiveDistrict }) {
  const districtPaths = {
    'bongaigaon': { d: 'M30 100 L120 80 L160 110 L140 160 L80 170 L40 140 Z', cx: 95, cy: 125 },
    'kamrup': { d: 'M140 160 L160 110 L250 100 L280 140 L260 190 L200 200 L160 185 Z', cx: 210, cy: 150 },
    'nagaon': { d: 'M260 190 L280 140 L370 130 L390 170 L370 210 L310 220 Z', cx: 330, cy: 175 },
    'sonitpur': { d: 'M250 100 L340 70 L390 95 L370 130 L280 140 Z', cx: 325, cy: 105 },
    'kaziranga': { d: 'M370 130 L390 95 L460 90 L480 130 L460 170 L390 170 Z', cx: 425, cy: 130 },
    'jorhat': { d: 'M370 210 L390 170 L460 170 L480 200 L465 240 L410 250 Z', cx: 430, cy: 210 },
    'sivasagar': { d: 'M465 240 L480 200 L540 195 L555 235 L535 265 L495 260 Z', cx: 515, cy: 230 },
    'dibrugarh': { d: 'M480 130 L540 110 L580 140 L570 190 L540 195 L480 200 L460 170 Z', cx: 530, cy: 160 },
  };

  return (
    <svg viewBox="0 0 620 300" className="w-full max-w-2xl mx-auto" style={{ filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.3))' }}>
      {Object.entries(districtPaths).map(([id, path]) => {
        const district = DISTRICTS.find(d => d.id === id);
        const isActive = activeDistrict === id;
        const isHighlight = district?.highlight;
        return (
          <g key={id} onMouseEnter={() => setActiveDistrict(id)} onMouseLeave={() => setActiveDistrict(null)} style={{ cursor: 'pointer' }}>
            <path d={path.d}
              fill={isActive ? '#C4915C' : isHighlight ? 'rgba(196,145,92,0.35)' : 'rgba(255,255,255,0.12)'}
              stroke={isActive ? '#E8C49A' : 'rgba(255,255,255,0.3)'} strokeWidth={isActive ? 2 : 1}
              style={{ transition: 'all 0.3s ease' }} />
            <text x={path.cx} y={path.cy} textAnchor="middle" dominantBaseline="middle"
              fill={isActive ? '#fff' : 'rgba(255,255,255,0.65)'} fontSize={isActive ? 11 : 9}
              fontFamily="Georgia, serif" style={{ transition: 'all 0.3s ease', pointerEvents: 'none' }}>
              {district?.name}
            </text>
            {isActive && (
              <text x={path.cx} y={path.cy + 15} textAnchor="middle" dominantBaseline="middle"
                fill="#E8C49A" fontSize={8} fontFamily="Georgia, serif" style={{ pointerEvents: 'none' }}>
                {district?.homestays} homestays
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function AnimatedNumber({ target, duration = 2000 }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else { setCount(Math.floor(start)); }
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return <span>{count.toLocaleString()}+</span>;
}

export default function AssamStatePage() {
  const [activeDistrict, setActiveDistrict] = useState(null);
  const activeData = DISTRICTS.find(d => d.id === activeDistrict);

  return (
    <div className="min-h-screen bg-[#141c24]" data-testid="assam-state-page">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#141c24]/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
            <Mountain className="w-5 h-5" />
            <span className="text-sm tracking-[0.15em] uppercase">GoHimalayan</span>
          </Link>
          <StateSelector current="assam" />
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/34758625/pexels-photo-34758625.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#141c24]/40 via-[#141c24]/40 to-[#141c24]/95" />
        </div>
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 border border-white/20 bg-white/5 backdrop-blur-sm px-5 py-2 rounded-full mb-10">
            <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            <span className="text-white/70 text-[10px] tracking-[0.4em] uppercase">Assam &middot; Gateway to the Northeast</span>
          </div>
          <h1 className="text-7xl sm:text-8xl md:text-9xl font-light text-white tracking-tight mb-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", textShadow: '0 4px 30px rgba(0,0,0,0.4)' }}>
            <AnimatedNumber target={TOTAL_HOMESTAYS} />
          </h1>
          <p className="text-white/70 text-sm tracking-[0.3em] uppercase mb-12">
            Licensed Homestays &middot; 33 Districts &middot; One Platform
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-white font-light italic tracking-tight leading-relaxed max-w-3xl mx-auto mb-14"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
            Where the Brahmaputra carries stories,<br />and every tea leaf holds a sunrise
          </h2>
        </div>
      </section>

      {/* Story */}
      <section className="pt-24 pb-8 px-6 relative" data-testid="story-section">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#C4915C] text-[10px] tracking-[0.5em] uppercase mb-6">The River Civilisation</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-white font-light italic tracking-tight leading-relaxed mb-8"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            A land shaped by water,<br />
            perfumed by tea, guarded by rhinos.
          </h2>
          <p className="text-white/50 text-sm sm:text-base leading-relaxed max-w-3xl mx-auto">
            On Majuli, the world's largest river island, Vaishnavite monks have chanted in 
            centuries-old satras while the Brahmaputra reshapes the land around them each monsoon. 
            In Kaziranga, one-horned rhinos graze in elephant grass taller than a man, their 
            silhouettes ancient against the Karbi hills. In Dibrugarh, colonial tea bungalows sit 
            among gardens that produce the world's most prized malty Orthodox teas — a cup here 
            tastes different because the air is different. In Sivasagar, the Ahom dynasty's 
            six-hundred-year legacy stands in mossy amphitheatres and towering temples built without 
            mortar. The Mishing people build their homes on stilts above the floodplain, weaving 
            silk in patterns that map the river's moods. This is Assam — where civilization learned 
            to float.
          </p>
        </div>
      </section>

      {/* Living Map */}
      <section className="py-24 px-6 relative" data-testid="living-map-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#C4915C] text-[10px] tracking-[0.5em] uppercase mb-4">The Living Map</p>
            <h2 className="text-3xl sm:text-4xl text-white font-light italic tracking-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              River islands to rhino country. One valley.
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <AssamMap activeDistrict={activeDistrict} setActiveDistrict={setActiveDistrict} />
            </div>
            <div className="space-y-6">
              {activeData ? (
                <div className="transition-all duration-300">
                  <div className="flex items-center gap-2 mb-3">
                    <p className="text-[#C4915C] text-[10px] tracking-[0.4em] uppercase">Region</p>
                    <span className="text-white/20 text-[10px]">&middot;</span>
                    <p className="text-white/30 text-[10px] tracking-[0.3em] uppercase">{activeData.division}</p>
                  </div>
                  <h3 className="text-4xl text-white font-light italic mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{activeData.name}</h3>
                  <div className="flex items-baseline gap-3 mb-6">
                    <span className="text-5xl text-white font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{activeData.homestays}</span>
                    <span className="text-white/40 text-sm tracking-wider uppercase">Registered Homestays</span>
                  </div>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-white/40" />
                      <span className="text-white/60 text-sm">{activeData.altitude}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {activeData.valleys.map(v => (
                        <span key={v} className="text-[10px] tracking-wider uppercase px-3 py-1.5 rounded-full border border-white/15 text-white/55">{v}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center lg:text-left">
                  <p className="text-white/20 text-sm italic mb-6">Hover over a region to explore</p>
                  <div className="grid grid-cols-2 gap-4">
                    {DISTRICTS.filter(d => d.highlight).map(d => (
                      <div key={d.id} className="border border-white/10 rounded-lg p-4 hover:border-white/25 transition-colors cursor-pointer"
                        onMouseEnter={() => setActiveDistrict(d.id)}>
                        <p className="text-white/80 text-sm mb-1">{d.name}</p>
                        <p className="text-2xl text-white font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{d.homestays}</p>
                        <p className="text-white/30 text-[10px] tracking-wider uppercase mt-1">homestays</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="border-t border-white/10 pt-6 mt-8">
                <div className="flex items-center justify-between">
                  <span className="text-white/45 text-xs tracking-wider uppercase">Total across Assam</span>
                  <span className="text-white text-lg font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{TOTAL_HOMESTAYS.toLocaleString()} homestays</span>
                </div>
                <div className="mt-3 h-1 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#C4915C] to-[#E8C49A] rounded-full" style={{ width: '100%', animation: 'expandBar 2s ease-out' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tier Funnel */}
      <section className="py-24 px-6 border-t border-white/10" data-testid="tier-funnel">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#C4915C] text-[10px] tracking-[0.5em] uppercase mb-4">The Homestay Index</p>
            <h2 className="text-3xl sm:text-4xl text-white font-light italic tracking-tight mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Three tiers. One ecosystem.</h2>
            <p className="text-white/50 text-sm max-w-xl mx-auto leading-relaxed">Every homestay begins as Registered. Through quality verification and partnership, they rise — from Silver to Gold.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TIERS.map((tier, i) => (
              <div key={tier.name} className="relative border border-white/10 rounded-xl p-8 hover:border-white/25 transition-all group" style={{ background: `linear-gradient(160deg, ${tier.color}12, transparent)` }}>
                <div className="absolute top-6 right-6 text-white/10 text-6xl font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{i + 1}</div>
                <tier.icon className="w-8 h-8 mb-6" style={{ color: tier.color }} />
                <h3 className="text-xl text-white font-light mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{tier.name}</h3>
                <p className="text-xs tracking-wider uppercase mb-4" style={{ color: tier.color }}>{tier.tagline}</p>
                <p className="text-white/45 text-sm leading-relaxed mb-6">{tier.desc}</p>
                <ul className="space-y-2.5">
                  {tier.features.map(f => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: tier.color }} />
                      <span className="text-white/55 text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
                {i < 2 && <div className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10"><ArrowRight className="w-5 h-5 text-white/10" /></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* B2G Section */}
      <section className="py-24 px-6 relative overflow-hidden" data-testid="b2g-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#141c24] via-[#182330] to-[#141c24]" />
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <p className="text-[#C4915C] text-[10px] tracking-[0.5em] uppercase mb-4">For Tourism Departments</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl text-white font-light italic tracking-tight mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              We digitize your homestay registry.<br /><span className="text-white/55">You focus on policy.</span>
            </h2>
            <p className="text-white/45 text-sm max-w-2xl mx-auto leading-relaxed">GoHimalayan serves as the technology and marketing partner for state tourism departments — bringing every licensed homestay across the Brahmaputra valley online, discoverable and bookable.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-20">
            {VALUE_PROPS.map(prop => (
              <div key={prop.title} className="border border-white/10 rounded-xl p-8 hover:border-white/20 transition-colors">
                <prop.icon className="w-6 h-6 text-[#C4915C] mb-5" />
                <h3 className="text-lg text-white font-light mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{prop.title}</h3>
                <p className="text-white/45 text-sm leading-relaxed">{prop.desc}</p>
              </div>
            ))}
          </div>
          <div className="border border-[#C4915C]/20 rounded-xl p-10 sm:p-14 text-center" style={{ background: 'linear-gradient(160deg, rgba(196,145,92,0.05), transparent)' }}>
            <p className="text-[#C4915C] text-[10px] tracking-[0.5em] uppercase mb-6">The Partnership Model</p>
            <h3 className="text-2xl sm:text-3xl text-white font-light italic mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Built to scale. Designed to empower.</h3>
            <div className="max-w-2xl mx-auto space-y-4 mb-10">
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#C4915C]/30 flex items-center justify-center flex-shrink-0"><span className="text-[#C4915C] text-xs">1</span></div>
                <p className="text-white/60 text-sm">Assam shares its licensed homestay registry — GoHimalayan digitizes and maps every property across 33 districts and the Brahmaputra valley</p>
              </div>
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#C4915C]/30 flex items-center justify-center flex-shrink-0"><span className="text-[#C4915C] text-xs">2</span></div>
                <p className="text-white/60 text-sm">Travellers discover, compare, and book directly — driving revenue into tea communities, river-island villages, and wildlife corridor homestays</p>
              </div>
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#C4915C]/30 flex items-center justify-center flex-shrink-0"><span className="text-[#C4915C] text-xs">3</span></div>
                <p className="text-white/60 text-sm">State receives real-time demand intelligence, Bihu-season analytics, and wildlife-corridor tourism planning dashboards</p>
              </div>
            </div>
            <a href="https://wa.me/919871155963?text=Hi%20GoHimalayan%2C%20I%27d%20like%20to%20discuss%20the%20State%20Partnership%20Program%20for%20Assam."
              className="inline-flex items-center gap-3 bg-[#C4915C] text-[#0a0f14] px-8 py-3.5 rounded-full text-xs font-semibold tracking-[0.15em] uppercase hover:bg-[#d4a87c] transition-colors"
              data-testid="b2g-cta" target="_blank" rel="noopener noreferrer">
              Begin Conversation <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <HimalayanPeaksFooter accentColor="#C4915C" />
      <style>{`@keyframes expandBar { from { width: 0; } to { width: 100%; } }`}</style>
    </div>
  );
}
